import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';

export default function useBrowserEvents() {
  const router = useRouter();
  const { track } = useAnalytics();
  const lastPath = useRef(router.asPath);
  const pageLoadTracked = useRef(false);

  const { email: loggedInUserEmail, travelAgency, loaded } = useSelector(
    (store) => store.user || {},
  );

  const safeTrack = useCallback((event, payload = {}) => {
    try {
      if (typeof track !== 'function') return;
      track(event, {
        loggedInUserEmail,
        travelAgencyId: travelAgency?.id || null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        pathname: router.pathname,
        ...payload,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Tracking error', e);
    }
  }, [track, loggedInUserEmail, travelAgency?.id, router.pathname]);

  useEffect(() => {
    if (!pageLoadTracked.current && loaded) {
      safeTrack('browser.page_load');
      pageLoadTracked.current = true;
    }

    const onRouteStart = (url) => { safeTrack('browser.route_start', { from: lastPath.current, to: url }); };
    const onRouteComplete = (url) => { safeTrack('browser.route_complete', { to: url }); lastPath.current = url; };
    const onPopState = ({ as }) => { safeTrack('browser.back_forward', { from: lastPath.current, to: as }); lastPath.current = as; return true; };
    const onUnload = () => { safeTrack('browser.unload', { url: typeof window !== 'undefined' ? window.location.href : null }); };

    router.events.on('routeChangeStart', onRouteStart);
    router.events.on('routeChangeComplete', onRouteComplete);
    router.beforePopState(onPopState);

    window.addEventListener('beforeunload', onUnload);

    return () => {
      router.events.off('routeChangeStart', onRouteStart);
      router.events.off('routeChangeComplete', onRouteComplete);
      router.beforePopState(() => true);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [router, loggedInUserEmail, travelAgency?.id, loaded, safeTrack]);
}
